/**
 * API クライアント — 全エンドポイント (35件)
 * 経験則 #1: 一覧は {複数形:[...], total} 展開 / 204 は null / パスはハイフン区切り複数形
 * 経験則 #20: required/optional を型に正確反映（types.ts 参照）
 */

import { apiGet, apiPost, apiPut, apiDelete } from './client'
import type {
  UserMe,
  RegisterRequest,
  LoginRequest,
  UpdateCertTypeRequest,
  ExamSpec,
  CertificationType,
  ListCategoriesResponse,
  ListStudyTextsResponse,
  StudyTextDetail,
  MarkViewedRequest,
  MarkViewedResponse,
  GetReadProgressResponse,
  QuestionForPractice,
  SubmitAnswerRequest,
  AnswerResult,
  QuestionWithAnswer,
  ListAnswerLogsResponse,
  ListReviewItemsResponse,
  ReviewItem,
  UpdateReviewItemRequest,
  StartMockExamRequest,
  MockExamSession,
  MockExamQuestion,
  SaveMockAnswerRequest,
  SaveMockAnswerResponse,
  PauseMockExamRequest,
  MockExamResult,
  ListMockExamResultsResponse,
  ProgressOverview,
  ListCategoryStatsResponse,
  ListWeakAreasResponse,
  ListAdminQuestionsResponse,
  QuestionForAdmin,
  ContentStatusUpdateRequest,
  ListAdminStudyTextsResponse,
  StudyTextForAdmin,
  CertificationTypeWithCommon,
  ContentStatus,
} from './types'

// =========================================================================
// 1. AUTH (5 エンドポイント)
// =========================================================================

/** POST /auth/register — E-AUTH-01 */
export async function registerUser(req: RegisterRequest): Promise<UserMe | null> {
  return apiPost<UserMe>('/auth/register', req)
}

/** POST /auth/login — E-AUTH-02 */
export async function loginUser(req: LoginRequest): Promise<UserMe | null> {
  return apiPost<UserMe>('/auth/login', req)
}

/** POST /auth/logout — E-AUTH-03 */
export async function logoutUser(): Promise<null> {
  return apiPost<null>('/auth/logout') as Promise<null>
}

/** GET /auth/me — E-AUTH-04 */
export async function getMe(): Promise<UserMe | null> {
  return apiGet<UserMe>('/auth/me')
}

/** PUT /auth/me/cert-type — E-AUTH-05 */
export async function updateCertType(req: UpdateCertTypeRequest): Promise<UserMe | null> {
  return apiPut<UserMe>('/auth/me/cert-type', req)
}

// =========================================================================
// 2. EXAM SPEC (1 エンドポイント)
// =========================================================================

/** GET /exam-specs/{certification_type} — E-SPEC-01 */
export async function getExamSpec(certificationType: CertificationType): Promise<ExamSpec | null> {
  return apiGet<ExamSpec>(`/exam-specs/${certificationType}`)
}

// =========================================================================
// 3. CATEGORIES (1 エンドポイント)
// =========================================================================

/** GET /categories?certification_type=... — E-CAT-01 */
export async function listCategories(
  certType?: CertificationTypeWithCommon,
): Promise<ListCategoriesResponse | null> {
  const q = certType ? `?certification_type=${certType}` : ''
  return apiGet<ListCategoriesResponse>(`/categories${q}`)
}

// =========================================================================
// 4. STUDY TEXTS (4 エンドポイント)
// =========================================================================

/** GET /study-texts — E-TXT-01 */
export async function listStudyTexts(params: {
  certification_type: CertificationType
  category_id?: string
}): Promise<ListStudyTextsResponse | null> {
  const q = new URLSearchParams({ certification_type: params.certification_type })
  if (params.category_id) q.set('category_id', params.category_id)
  return apiGet<ListStudyTextsResponse>(`/study-texts?${q.toString()}`)
}

/** GET /study-texts/{id} — E-TXT-02 */
export async function getStudyText(id: string): Promise<StudyTextDetail | null> {
  return apiGet<StudyTextDetail>(`/study-texts/${id}`)
}

/** POST /study-texts/{id}/view — E-TXT-03 */
export async function markStudyTextViewed(
  id: string,
  req?: MarkViewedRequest,
): Promise<MarkViewedResponse | null> {
  return apiPost<MarkViewedResponse>(`/study-texts/${id}/view`, req ?? {})
}

/** GET /study-texts/read-progress — E-TXT-04 */
export async function getReadProgress(
  certType: CertificationType,
): Promise<GetReadProgressResponse | null> {
  return apiGet<GetReadProgressResponse>(`/study-texts/read-progress?certification_type=${certType}`)
}

// =========================================================================
// 5. QUESTIONS (3 エンドポイント)
// =========================================================================

/** GET /questions/next — E-Q-01 */
export async function getNextQuestion(params: {
  certification_type: CertificationType
  category_id?: string
  mode?: 'practice' | 'review'
}): Promise<QuestionForPractice | null> {
  const q = new URLSearchParams({ certification_type: params.certification_type })
  if (params.category_id) q.set('category_id', params.category_id)
  if (params.mode) q.set('mode', params.mode)
  return apiGet<QuestionForPractice>(`/questions/next?${q.toString()}`)
}

/** POST /questions/{id}/answer — E-Q-02 */
export async function submitAnswer(
  questionId: string,
  req: SubmitAnswerRequest,
): Promise<AnswerResult | null> {
  return apiPost<AnswerResult>(`/questions/${questionId}/answer`, req)
}

/** GET /questions/{id} — E-Q-03 */
export async function getQuestion(id: string): Promise<QuestionWithAnswer | null> {
  return apiGet<QuestionWithAnswer>(`/questions/${id}`)
}

// =========================================================================
// 6. ANSWER LOGS (2 エンドポイント)
// =========================================================================

/** GET /answer-logs — E-AL-01 */
export async function listAnswerLogs(params?: {
  certification_type?: CertificationType
  limit?: number
  offset?: number
}): Promise<ListAnswerLogsResponse | null> {
  const q = new URLSearchParams()
  if (params?.certification_type) q.set('certification_type', params.certification_type)
  if (params?.limit !== undefined) q.set('limit', String(params.limit))
  if (params?.offset !== undefined) q.set('offset', String(params.offset))
  const qs = q.toString()
  return apiGet<ListAnswerLogsResponse>(`/answer-logs${qs ? `?${qs}` : ''}`)
}

/** DELETE /answer-logs/{id} — E-AL-02 */
export async function deleteAnswerLog(id: string): Promise<null> {
  return apiDelete(`/answer-logs/${id}`)
}

// =========================================================================
// 7. REVIEW ITEMS (3 エンドポイント)
// =========================================================================

/** GET /review-items — E-REV-01 */
export async function listReviewItems(
  certType?: CertificationType,
): Promise<ListReviewItemsResponse | null> {
  const q = certType ? `?certification_type=${certType}` : ''
  return apiGet<ListReviewItemsResponse>(`/review-items${q}`)
}

/** PUT /review-items/{id} — E-REV-02 (後方互換: 新規フロントから呼ばない) */
export async function updateReviewItem(
  id: string,
  req: UpdateReviewItemRequest,
): Promise<ReviewItem | null> {
  return apiPut<ReviewItem>(`/review-items/${id}`, req)
}

/** GET /review-items/{id}/question — E-REV-03 */
export async function getReviewQuestion(id: string): Promise<QuestionForPractice | null> {
  return apiGet<QuestionForPractice>(`/review-items/${id}/question`)
}

// =========================================================================
// 8. MOCK EXAM (8 エンドポイント)
// =========================================================================

/** POST /mock-exams — E-MOCK-01 */
export async function startMockExam(req: StartMockExamRequest): Promise<MockExamSession | null> {
  return apiPost<MockExamSession>('/mock-exams', req)
}

/** GET /mock-exams/active — E-MOCK-02 */
export async function getActiveMockExam(): Promise<MockExamSession | null> {
  return apiGet<MockExamSession>('/mock-exams/active')
}

/** GET /mock-exams/{session_id}/questions/{question_order} — E-MOCK-03 */
export async function getMockExamQuestion(
  sessionId: string,
  questionOrder: number,
): Promise<MockExamQuestion | null> {
  return apiGet<MockExamQuestion>(`/mock-exams/${sessionId}/questions/${questionOrder}`)
}

/** POST /mock-exams/{session_id}/answers — E-MOCK-04 */
export async function saveMockAnswer(
  sessionId: string,
  req: SaveMockAnswerRequest,
): Promise<SaveMockAnswerResponse | null> {
  return apiPost<SaveMockAnswerResponse>(`/mock-exams/${sessionId}/answers`, req)
}

/** POST /mock-exams/{session_id}/pause — E-MOCK-05 */
export async function pauseMockExam(
  sessionId: string,
  req: PauseMockExamRequest,
): Promise<MockExamSession | null> {
  return apiPost<MockExamSession>(`/mock-exams/${sessionId}/pause`, req)
}

/** POST /mock-exams/{session_id}/submit — E-MOCK-06 */
export async function submitMockExam(sessionId: string): Promise<MockExamResult | null> {
  return apiPost<MockExamResult>(`/mock-exams/${sessionId}/submit`)
}

/** POST /mock-exams/{session_id}/discard — E-MOCK-07 */
export async function discardMockExam(sessionId: string): Promise<null> {
  return apiPost<null>(`/mock-exams/${sessionId}/discard`) as Promise<null>
}

/** GET /mock-exams/results — E-MOCK-08 */
export async function listMockExamResults(
  certType?: CertificationType,
): Promise<ListMockExamResultsResponse | null> {
  const q = certType ? `?certification_type=${certType}` : ''
  return apiGet<ListMockExamResultsResponse>(`/mock-exams/results${q}`)
}

// =========================================================================
// 9. PROGRESS / STATS (3 エンドポイント)
// =========================================================================

/** GET /progress/overview — E-PROG-01 */
export async function getProgressOverview(
  certType: CertificationType,
): Promise<ProgressOverview | null> {
  return apiGet<ProgressOverview>(`/progress/overview?certification_type=${certType}`)
}

/** GET /progress/categories — E-PROG-02 */
export async function getCategoryStats(
  certType: CertificationType,
): Promise<ListCategoryStatsResponse | null> {
  return apiGet<ListCategoryStatsResponse>(`/progress/categories?certification_type=${certType}`)
}

/** GET /progress/weak-areas — E-PROG-03 */
export async function getWeakAreas(
  certType: CertificationType,
  threshold?: number,
): Promise<ListWeakAreasResponse | null> {
  const q = new URLSearchParams({ certification_type: certType })
  if (threshold !== undefined) q.set('threshold', String(threshold))
  return apiGet<ListWeakAreasResponse>(`/progress/weak-areas?${q.toString()}`)
}

// =========================================================================
// 10. ADMIN — QUESTIONS (3 エンドポイント)
// =========================================================================

/** GET /admin/questions — E-ADM-Q-01 */
export async function adminListQuestions(params?: {
  status?: ContentStatus
  certification_type?: CertificationTypeWithCommon
  limit?: number
  offset?: number
}): Promise<ListAdminQuestionsResponse | null> {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.certification_type) q.set('certification_type', params.certification_type)
  if (params?.limit !== undefined) q.set('limit', String(params.limit))
  if (params?.offset !== undefined) q.set('offset', String(params.offset))
  const qs = q.toString()
  return apiGet<ListAdminQuestionsResponse>(`/admin/questions${qs ? `?${qs}` : ''}`)
}

/** PUT /admin/questions/{id}/status — E-ADM-Q-02 */
export async function adminUpdateQuestionStatus(
  id: string,
  req: ContentStatusUpdateRequest,
): Promise<QuestionForAdmin | null> {
  return apiPut<QuestionForAdmin>(`/admin/questions/${id}/status`, req)
}

/** GET /admin/questions/{id} — E-ADM-Q-03 */
export async function adminGetQuestion(id: string): Promise<QuestionForAdmin | null> {
  return apiGet<QuestionForAdmin>(`/admin/questions/${id}`)
}

// =========================================================================
// 11. ADMIN — STUDY TEXTS (2 エンドポイント)
// =========================================================================

/** GET /admin/study-texts — E-ADM-TXT-01 */
export async function adminListStudyTexts(params?: {
  status?: ContentStatus
  certification_type?: CertificationTypeWithCommon
  limit?: number
  offset?: number
}): Promise<ListAdminStudyTextsResponse | null> {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.certification_type) q.set('certification_type', params.certification_type)
  if (params?.limit !== undefined) q.set('limit', String(params.limit))
  if (params?.offset !== undefined) q.set('offset', String(params.offset))
  const qs = q.toString()
  return apiGet<ListAdminStudyTextsResponse>(`/admin/study-texts${qs ? `?${qs}` : ''}`)
}

/** PUT /admin/study-texts/{id}/status — E-ADM-TXT-02 */
export async function adminUpdateStudyTextStatus(
  id: string,
  req: ContentStatusUpdateRequest,
): Promise<StudyTextForAdmin | null> {
  return apiPut<StudyTextForAdmin>(`/admin/study-texts/${id}/status`, req)
}
